// src/admin/AdminContent.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from 'src/lib/supabase';

const UI = {
  spacing: { xs: 6, sm: 8, md: 12, lg: 16, xl: 24 },
  radius: { sm: 6, md: 8, lg: 12 },
  colors: {
    primary: "#0F172A",
    secondary: "#334155",
    muted: "#64748B",
    border: "#E2E8F0",
    bg: "#F8FAFC",
    success: "#059669",
    warning: "#D97706",
    danger: "#DC2626",
    info: "#3B82F6"
  }
};

const sectionTypes = [
  { value: 'hero', label: 'Hero Banner' },
  { value: 'text_block', label: 'Text Block' },
  { value: 'service_list', label: 'Service List' },
  { value: 'faq', label: 'FAQ Section' },
  { value: 'testimonials', label: 'Testimonials' },
  { value: 'cta', label: 'Call to Action' },
  { value: 'image_banner', label: 'Image Banner' },
  { value: 'category_links', label: 'Category Links' }
];

const defaultSectionData = {
  hero: { heading: '', subheading: '', ctaText: '', ctaLink: '', backgroundImage: '', backgroundColor: '#0F172A', textColor: '#ffffff' },
  text_block: { heading: '', content: '', align: 'left' },
  service_list: { heading: '', category: '', limit: 6, showPrices: true },
  faq: { heading: '', items: [] },
  testimonials: { heading: '', items: [] },
  cta: { heading: '', subheading: '', buttonText: '', buttonLink: '', backgroundColor: '#0F172A', textColor: '#ffffff' },
  image_banner: { imageUrl: '', altText: '', caption: '', linkUrl: '', aspectRatio: '16/9' },
  category_links: { heading: '', items: [] }
};

function SectionEditor({ section, onSave, onClose }) {
  const [data, setData] = useState(section?.data || defaultSectionData[section?.type] || {});
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave({ ...section, data });
    setSaving(false);
  };

  const renderFields = () => {
    switch (section?.type) {
      case 'hero':
        return (
          <>
            <input type="text" placeholder="Heading" value={data.heading || ''} onChange={e => setData({...data, heading: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="Subheading" value={data.subheading || ''} onChange={e => setData({...data, subheading: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="CTA Text" value={data.ctaText || ''} onChange={e => setData({...data, ctaText: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="CTA Link" value={data.ctaLink || ''} onChange={e => setData({...data, ctaLink: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="Background Image URL" value={data.backgroundImage || ''} onChange={e => setData({...data, backgroundImage: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="Background Color (hex)" value={data.backgroundColor || '#0F172A'} onChange={e => setData({...data, backgroundColor: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="Text Color (hex)" value={data.textColor || '#ffffff'} onChange={e => setData({...data, textColor: e.target.value})} style={inputStyle} />
          </>
        );
      case 'text_block':
        return (
          <>
            <input type="text" placeholder="Heading" value={data.heading || ''} onChange={e => setData({...data, heading: e.target.value})} style={inputStyle} />
            <textarea placeholder="Content (HTML allowed)" value={data.content || ''} onChange={e => setData({...data, content: e.target.value})} rows={6} style={textareaStyle} />
            <select value={data.align || 'left'} onChange={e => setData({...data, align: e.target.value})} style={inputStyle}>
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </>
        );
      case 'service_list':
        return (
          <>
            <input type="text" placeholder="Heading" value={data.heading || ''} onChange={e => setData({...data, heading: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="Category" value={data.category || ''} onChange={e => setData({...data, category: e.target.value})} style={inputStyle} />
            <input type="number" placeholder="Limit" value={data.limit || 6} onChange={e => setData({...data, limit: parseInt(e.target.value)})} style={inputStyle} />
            <label style={checkboxStyle}>
              <input type="checkbox" checked={data.showPrices !== false} onChange={e => setData({...data, showPrices: e.target.checked})} />
              Show Prices
            </label>
          </>
        );
      case 'faq':
        return (
          <>
            <input type="text" placeholder="Heading" value={data.heading || ''} onChange={e => setData({...data, heading: e.target.value})} style={inputStyle} />
            <div style={{ marginTop: UI.spacing.md }}>
              <div style={{ fontWeight: 600, marginBottom: UI.spacing.sm }}>FAQ Items</div>
              {data.items?.map((item, idx) => (
                <div key={idx} style={{ marginBottom: UI.spacing.md, padding: UI.spacing.md, background: UI.colors.bg, borderRadius: UI.radius.md }}>
                  <input type="text" placeholder="Question" value={item.q} onChange={e => {
                    const newItems = [...data.items];
                    newItems[idx].q = e.target.value;
                    setData({...data, items: newItems});
                  }} style={inputStyle} />
                  <textarea placeholder="Answer" value={item.a} onChange={e => {
                    const newItems = [...data.items];
                    newItems[idx].a = e.target.value;
                    setData({...data, items: newItems});
                  }} rows={2} style={textareaStyle} />
                  <button onClick={() => {
                    const newItems = data.items.filter((_, i) => i !== idx);
                    setData({...data, items: newItems});
                  }} style={deleteButtonStyle}>Delete</button>
                </div>
              ))}
              <button onClick={() => setData({...data, items: [...(data.items || []), { q: '', a: '' }]})} style={addButtonStyle}>+ Add Question</button>
            </div>
          </>
        );
      case 'testimonials':
        return (
          <>
            <input type="text" placeholder="Heading" value={data.heading || ''} onChange={e => setData({...data, heading: e.target.value})} style={inputStyle} />
            <div style={{ marginTop: UI.spacing.md }}>
              <div style={{ fontWeight: 600, marginBottom: UI.spacing.sm }}>Testimonials</div>
              {data.items?.map((item, idx) => (
                <div key={idx} style={{ marginBottom: UI.spacing.md, padding: UI.spacing.md, background: UI.colors.bg, borderRadius: UI.radius.md }}>
                  <input type="text" placeholder="Name" value={item.name || ''} onChange={e => {
                    const newItems = [...data.items];
                    newItems[idx].name = e.target.value;
                    setData({...data, items: newItems});
                  }} style={inputStyle} />
                  <input type="text" placeholder="Location" value={item.location || ''} onChange={e => {
                    const newItems = [...data.items];
                    newItems[idx].location = e.target.value;
                    setData({...data, items: newItems});
                  }} style={inputStyle} />
                  <textarea placeholder="Testimonial text" value={item.text || ''} onChange={e => {
                    const newItems = [...data.items];
                    newItems[idx].text = e.target.value;
                    setData({...data, items: newItems});
                  }} rows={2} style={textareaStyle} />
                  <input type="number" placeholder="Rating (1-5)" value={item.rating || 5} onChange={e => {
                    const newItems = [...data.items];
                    newItems[idx].rating = parseInt(e.target.value);
                    setData({...data, items: newItems});
                  }} style={inputStyle} />
                  <button onClick={() => {
                    const newItems = data.items.filter((_, i) => i !== idx);
                    setData({...data, items: newItems});
                  }} style={deleteButtonStyle}>Delete</button>
                </div>
              ))}
              <button onClick={() => setData({...data, items: [...(data.items || []), { name: '', location: '', text: '', rating: 5 }]})} style={addButtonStyle}>+ Add Testimonial</button>
            </div>
          </>
        );
      case 'cta':
        return (
          <>
            <input type="text" placeholder="Heading" value={data.heading || ''} onChange={e => setData({...data, heading: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="Subheading" value={data.subheading || ''} onChange={e => setData({...data, subheading: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="Button Text" value={data.buttonText || ''} onChange={e => setData({...data, buttonText: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="Button Link" value={data.buttonLink || ''} onChange={e => setData({...data, buttonLink: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="Background Color (hex)" value={data.backgroundColor || '#0F172A'} onChange={e => setData({...data, backgroundColor: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="Text Color (hex)" value={data.textColor || '#ffffff'} onChange={e => setData({...data, textColor: e.target.value})} style={inputStyle} />
          </>
        );
      case 'image_banner':
        return (
          <>
            <input type="text" placeholder="Image URL" value={data.imageUrl || ''} onChange={e => setData({...data, imageUrl: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="Alt Text" value={data.altText || ''} onChange={e => setData({...data, altText: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="Caption" value={data.caption || ''} onChange={e => setData({...data, caption: e.target.value})} style={inputStyle} />
            <input type="text" placeholder="Link URL" value={data.linkUrl || ''} onChange={e => setData({...data, linkUrl: e.target.value})} style={inputStyle} />
            <select value={data.aspectRatio || '16/9'} onChange={e => setData({...data, aspectRatio: e.target.value})} style={inputStyle}>
              <option value="16/9">16:9</option>
              <option value="4/3">4:3</option>
              <option value="1/1">1:1</option>
            </select>
          </>
        );
      case 'category_links':
        return (
          <>
            <input type="text" placeholder="Heading" value={data.heading || ''} onChange={e => setData({...data, heading: e.target.value})} style={inputStyle} />
            <div style={{ marginTop: UI.spacing.md }}>
              <div style={{ fontWeight: 600, marginBottom: UI.spacing.sm }}>Category Links</div>
              {data.items?.map((item, idx) => (
                <div key={idx} style={{ marginBottom: UI.spacing.md, padding: UI.spacing.md, background: UI.colors.bg, borderRadius: UI.radius.md }}>
                  <input type="text" placeholder="Label" value={item.label || ''} onChange={e => {
                    const newItems = [...data.items];
                    newItems[idx].label = e.target.value;
                    setData({...data, items: newItems});
                  }} style={inputStyle} />
                  <input type="text" placeholder="Icon (emoji)" value={item.icon || '📦'} onChange={e => {
                    const newItems = [...data.items];
                    newItems[idx].icon = e.target.value;
                    setData({...data, items: newItems});
                  }} style={inputStyle} />
                  <input type="text" placeholder="Link URL" value={item.link || ''} onChange={e => {
                    const newItems = [...data.items];
                    newItems[idx].link = e.target.value;
                    setData({...data, items: newItems});
                  }} style={inputStyle} />
                  <button onClick={() => {
                    const newItems = data.items.filter((_, i) => i !== idx);
                    setData({...data, items: newItems});
                  }} style={deleteButtonStyle}>Delete</button>
                </div>
              ))}
              <button onClick={() => setData({...data, items: [...(data.items || []), { label: '', icon: '📦', link: '' }]})} style={addButtonStyle}>+ Add Category Link</button>
            </div>
          </>
        );
      default:
        return <div>Editor for this section type coming soon</div>;
    }
  };

  const inputStyle = {
    width: '100%',
    padding: UI.spacing.md,
    border: `1px solid ${UI.colors.border}`,
    borderRadius: UI.radius.md,
    fontSize: 13,
    marginBottom: UI.spacing.md,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxSizing: 'border-box'
  };

  const textareaStyle = {
    ...inputStyle,
    resize: 'vertical'
  };

  const checkboxStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: UI.spacing.sm,
    marginBottom: UI.spacing.md,
    fontSize: 13
  };

  const deleteButtonStyle = {
    padding: `${UI.spacing.xs}px ${UI.spacing.md}px`,
    background: UI.colors.danger,
    border: 'none',
    borderRadius: UI.radius.sm,
    fontSize: 12,
    fontWeight: 600,
    color: '#FFFFFF',
    cursor: 'pointer'
  };

  const addButtonStyle = {
    padding: `${UI.spacing.sm}px ${UI.spacing.md}px`,
    background: UI.colors.primary,
    border: 'none',
    borderRadius: UI.radius.sm,
    fontSize: 12,
    fontWeight: 600,
    color: '#FFFFFF',
    cursor: 'pointer'
  };

  return (
    <div style={{
      position: 'fixed',
      right: 0,
      top: 0,
      bottom: 0,
      width: 480,
      background: '#FFFFFF',
      boxShadow: '-4px 0 40px rgba(0,0,0,0.14)',
      zIndex: 300,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <div style={{ padding: UI.spacing.lg, borderBottom: `1px solid ${UI.colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700 }}>Edit {section?.type}</div>
        <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: UI.radius.sm, border: `1px solid ${UI.colors.border}`, background: '#FFFFFF', cursor: 'pointer' }}>✕</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: UI.spacing.lg }}>
        {renderFields()}
      </div>
      <div style={{ padding: UI.spacing.lg, borderTop: `1px solid ${UI.colors.border}`, display: 'flex', gap: UI.spacing.md }}>
        <button onClick={onClose} style={{ flex: 1, padding: UI.spacing.md, background: '#FFFFFF', border: `1px solid ${UI.colors.border}`, borderRadius: UI.radius.md, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: UI.spacing.md, background: UI.colors.primary, border: 'none', borderRadius: UI.radius.md, fontSize: 13, fontWeight: 600, color: '#FFFFFF', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving...' : 'Save'}</button>
      </div>
    </div>
  );
}

// New Page Modal Component
function NewPageModal({ isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    status: 'draft'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }
    
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        zIndex: 399
      }} />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 480,
        background: '#FFFFFF',
        borderRadius: UI.radius.lg,
        zIndex: 400,
        padding: UI.spacing.xl
      }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: UI.spacing.lg }}>Create New Page</h3>
        
        <div style={{ marginBottom: UI.spacing.md }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Page Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => {
              const title = e.target.value;
              const slug = title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
              setFormData({ title, slug, status: formData.status });
            }}
            placeholder="e.g., Cleaning Services in Nairobi"
            style={{
              width: '100%',
              padding: UI.spacing.md,
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.md,
              fontSize: 13
            }}
          />
        </div>
        
        <div style={{ marginBottom: UI.spacing.md }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Slug (URL)</label>
          <input
            type="text"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
            placeholder="cleaning-services-in-nairobi"
            style={{
              width: '100%',
              padding: UI.spacing.md,
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.md,
              fontSize: 13
            }}
          />
          <div style={{ fontSize: 11, color: UI.colors.muted, marginTop: UI.spacing.xs }}>
            Your page will be available at: /{formData.slug}
          </div>
        </div>
        
        <div style={{ marginBottom: UI.spacing.lg }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Status</label>
          <div style={{ display: 'flex', gap: UI.spacing.md }}>
            <button
              onClick={() => setFormData({ ...formData, status: 'draft' })}
              style={{
                flex: 1,
                padding: UI.spacing.md,
                background: formData.status === 'draft' ? UI.colors.primary : '#FFFFFF',
                border: `1px solid ${UI.colors.border}`,
                borderRadius: UI.radius.md,
                fontSize: 13,
                fontWeight: 600,
                color: formData.status === 'draft' ? '#FFFFFF' : UI.colors.secondary,
                cursor: 'pointer'
              }}
            >
              Draft
            </button>
            <button
              onClick={() => setFormData({ ...formData, status: 'published' })}
              style={{
                flex: 1,
                padding: UI.spacing.md,
                background: formData.status === 'published' ? UI.colors.primary : '#FFFFFF',
                border: `1px solid ${UI.colors.border}`,
                borderRadius: UI.radius.md,
                fontSize: 13,
                fontWeight: 600,
                color: formData.status === 'published' ? '#FFFFFF' : UI.colors.secondary,
                cursor: 'pointer'
              }}
            >
              Published
            </button>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: UI.spacing.md }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: UI.spacing.md,
              background: '#FFFFFF',
              border: `1px solid ${UI.colors.border}`,
              borderRadius: UI.radius.md,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              flex: 1,
              padding: UI.spacing.md,
              background: UI.colors.primary,
              border: 'none',
              borderRadius: UI.radius.md,
              fontSize: 13,
              fontWeight: 600,
              color: '#FFFFFF',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? 'Creating...' : 'Create Page'}
          </button>
        </div>
      </div>
    </>
  );
}

export default function AdminContent() {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('content');
  const [toast, setToast] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newPageModalOpen, setNewPageModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    status: 'draft',
    meta_title: '',
    meta_description: '',
    schema_type: 'WebPage',
    category: '',
    location: ''
  });
  
  const slugTimeout = useRef(null);

  useEffect(() => {
    loadPages();
  }, []);

  useEffect(() => {
    if (selectedPage) {
      loadSections(selectedPage.id);
      setFormData({
        title: selectedPage.title || '',
        slug: selectedPage.slug || '',
        description: selectedPage.description || '',
        status: selectedPage.status || 'draft',
        meta_title: selectedPage.meta_title || '',
        meta_description: selectedPage.meta_description || '',
        schema_type: selectedPage.schema_type || 'WebPage',
        category: selectedPage.category || '',
        location: selectedPage.location || ''
      });
    }
  }, [selectedPage]);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadPages = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('content_pages').select('*').order('created_at', { ascending: false });
    if (!error) setPages(data || []);
    setLoading(false);
  };

  const loadSections = async (pageId) => {
    const { data, error } = await supabase.from('content_sections').select('*').eq('page_id', pageId).order('position', { ascending: true });
    if (!error) setSections(data || []);
  };

  const handleCreatePage = async (pageData) => {
    try {
      const { data, error } = await supabase
        .from('content_pages')
        .insert({
          title: pageData.title,
          slug: pageData.slug,
          status: pageData.status,
          is_auto_generated: false
        })
        .select()
        .single();
      
      if (error) throw error;
      
      setPages([data, ...pages]);
      setSelectedPage(data);
      setNewPageModalOpen(false);
      showToast(`Page "${data.title}" created successfully`);
    } catch (error) {
      console.error('Create page error:', error);
      showToast(`Failed to create page: ${error.message}`, 'error');
    }
  };

  const handleSavePage = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('content_pages')
        .update({
          title: formData.title,
          slug: formData.slug,
          description: formData.description,
          status: formData.status,
          meta_title: formData.meta_title,
          meta_description: formData.meta_description,
          schema_type: formData.schema_type,
          category: formData.category,
          location: formData.location,
          updated_at: new Date()
        })
        .eq('id', selectedPage.id);
      
      if (error) throw error;
      
      setPages(pages.map(p => p.id === selectedPage.id ? { ...p, ...formData } : p));
      setSelectedPage({ ...selectedPage, ...formData });
      showToast('Page saved successfully');
    } catch (error) {
      showToast(`Error: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePage = async () => {
    if (!window.confirm('Delete this page? This action cannot be undone.')) return;
    try {
      const { error } = await supabase.from('content_pages').delete().eq('id', selectedPage.id);
      if (error) throw error;
      showToast('Page deleted');
      setSelectedPage(null);
      loadPages();
    } catch (error) {
      showToast(`Error: ${error.message}`, 'error');
    }
  };

  const handleAddSection = async (type) => {
    const newPosition = sections.length;
    const { data, error } = await supabase
      .from('content_sections')
      .insert({
        page_id: selectedPage.id,
        type,
        position: newPosition,
        data: defaultSectionData[type]
      })
      .select()
      .single();
      
    if (!error) {
      setSections([...sections, data]);
      showToast(`Section added`);
    } else {
      showToast(`Failed to add section: ${error.message}`, 'error');
    }
  };

  const handleUpdateSection = async (updatedSection) => {
    const { error } = await supabase
      .from('content_sections')
      .update({ data: updatedSection.data })
      .eq('id', updatedSection.id);
      
    if (!error) {
      setSections(sections.map(s => s.id === updatedSection.id ? updatedSection : s));
      setEditingSection(null);
      showToast('Section updated');
    } else {
      showToast(`Failed to update section: ${error.message}`, 'error');
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section?')) return;
    const { error } = await supabase.from('content_sections').delete().eq('id', sectionId);
    if (!error) {
      setSections(sections.filter(s => s.id !== sectionId));
      showToast('Section deleted');
    }
  };

  const handleMoveSection = async (sectionId, direction) => {
    const index = sections.findIndex(s => s.id === sectionId);
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    const newSections = [...sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    
    for (let i = 0; i < newSections.length; i++) {
      await supabase.from('content_sections').update({ position: i }).eq('id', newSections[i].id);
    }
    setSections(newSections);
    showToast('Section reordered');
  };

  const handleTitleChange = (title) => {
    setFormData({ ...formData, title });
    if (slugTimeout.current) clearTimeout(slugTimeout.current);
    slugTimeout.current = setTimeout(() => {
      const slug = title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }, 300);
  };

  const filteredPages = useMemo(() => {
    if (!searchQuery) return pages;
    return pages.filter(p => p.title.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [pages, searchQuery]);

  const totalViews = pages.reduce((sum, p) => sum + (p.page_views || 0), 0);
  const published = pages.filter(p => p.status === 'published').length;
  const drafts = pages.filter(p => p.status === 'draft').length;

  return (
    <div>
      {/* KPI Strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: UI.spacing.md, marginBottom: UI.spacing.xl }}>
        {[
          { label: 'Total Pages', value: pages.length },
          { label: 'Published', value: published },
          { label: 'Drafts', value: drafts },
          { label: 'Total Views', value: totalViews.toLocaleString() }
        ].map(kpi => (
          <div key={kpi.label} style={{ background: '#FFFFFF', border: `1px solid ${UI.colors.border}`, borderRadius: UI.radius.lg, padding: `${UI.spacing.lg}px ${UI.spacing.md}px` }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: UI.colors.muted, marginBottom: UI.spacing.sm }}>{kpi.label}</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 700, color: UI.colors.primary }}>{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Main Layout */}
      <div style={{ display: 'flex', gap: UI.spacing.lg, minHeight: 600 }}>
        {/* Left Panel - Page List */}
        <div style={{ width: 320, background: '#FFFFFF', borderRadius: UI.radius.lg, border: `1px solid ${UI.colors.border}`, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: UI.spacing.lg, borderBottom: `1px solid ${UI.colors.border}` }}>
            <input
              type="text"
              placeholder="Search pages..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ width: '100%', padding: UI.spacing.md, border: `1px solid ${UI.colors.border}`, borderRadius: UI.radius.md, fontSize: 13 }}
            />
            <button
              onClick={() => setNewPageModalOpen(true)}
              style={{ width: '100%', marginTop: UI.spacing.md, padding: UI.spacing.md, background: UI.colors.primary, border: 'none', borderRadius: UI.radius.md, fontSize: 13, fontWeight: 600, color: '#FFFFFF', cursor: 'pointer' }}
            >
              + New Page
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filteredPages.map(page => (
              <div
                key={page.id}
                onClick={() => setSelectedPage(page)}
                style={{
                  padding: UI.spacing.lg,
                  borderBottom: `1px solid ${UI.colors.border}`,
                  cursor: 'pointer',
                  background: selectedPage?.id === page.id ? '#F0FDF4' : 'transparent',
                  borderLeft: selectedPage?.id === page.id ? `3px solid ${UI.colors.primary}` : '3px solid transparent'
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: UI.spacing.xs }}>{page.title}</div>
                <div style={{ fontSize: 11, color: UI.colors.muted }}>{page.slug}</div>
                <div style={{ marginTop: UI.spacing.xs }}>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: UI.radius.sm, background: page.status === 'published' ? '#D1FAE5' : '#F1F5F9', color: page.status === 'published' ? '#059669' : '#64748B' }}>{page.status}</span>
                  <span style={{ fontSize: 11, marginLeft: UI.spacing.sm, color: UI.colors.muted }}>👁 {page.page_views || 0}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Page Editor */}
        <div style={{ flex: 1, background: '#FFFFFF', borderRadius: UI.radius.lg, border: `1px solid ${UI.colors.border}`, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {!selectedPage ? (
            <div style={{ textAlign: 'center', padding: 60, color: UI.colors.muted }}>Select a page to edit or click "New Page" to create one</div>
          ) : (
            <>
              <div style={{ padding: UI.spacing.lg, borderBottom: `1px solid ${UI.colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: UI.spacing.md }}>
                  <button onClick={() => setActiveTab('content')} style={{ padding: `${UI.spacing.sm}px ${UI.spacing.lg}px`, background: activeTab === 'content' ? UI.colors.primary : 'transparent', border: 'none', borderRadius: UI.radius.md, fontSize: 13, fontWeight: 600, color: activeTab === 'content' ? '#FFFFFF' : UI.colors.secondary, cursor: 'pointer' }}>Content</button>
                  <button onClick={() => setActiveTab('seo')} style={{ padding: `${UI.spacing.sm}px ${UI.spacing.lg}px`, background: activeTab === 'seo' ? UI.colors.primary : 'transparent', border: 'none', borderRadius: UI.radius.md, fontSize: 13, fontWeight: 600, color: activeTab === 'seo' ? '#FFFFFF' : UI.colors.secondary, cursor: 'pointer' }}>SEO</button>
                </div>
                <div style={{ display: 'flex', gap: UI.spacing.sm }}>
                  <button onClick={handleSavePage} disabled={saving} style={{ padding: `${UI.spacing.sm}px ${UI.spacing.lg}px`, background: UI.colors.success, border: 'none', borderRadius: UI.radius.md, fontSize: 13, fontWeight: 600, color: '#FFFFFF', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>Save</button>
                  <button onClick={() => window.open(`/${formData.slug}`, '_blank')} style={{ padding: `${UI.spacing.sm}px ${UI.spacing.lg}px`, background: UI.colors.info, border: 'none', borderRadius: UI.radius.md, fontSize: 13, fontWeight: 600, color: '#FFFFFF', cursor: 'pointer' }}>Preview</button>
                  <button onClick={handleDeletePage} style={{ padding: `${UI.spacing.sm}px ${UI.spacing.lg}px`, background: UI.colors.danger, border: 'none', borderRadius: UI.radius.md, fontSize: 13, fontWeight: 600, color: '#FFFFFF', cursor: 'pointer' }}>Delete</button>
                </div>
              </div>
              
              <div style={{ flex: 1, overflowY: 'auto', padding: UI.spacing.lg }}>
                {activeTab === 'content' ? (
                  <>
                    <div style={{ marginBottom: UI.spacing.lg }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Title</label>
                      <input type="text" value={formData.title} onChange={e => handleTitleChange(e.target.value)} style={{ width: '100%', padding: UI.spacing.md, border: `1px solid ${UI.colors.border}`, borderRadius: UI.radius.md, fontSize: 13 }} />
                    </div>
                    <div style={{ marginBottom: UI.spacing.lg }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Slug</label>
                      <input type="text" value={formData.slug} onChange={e => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} style={{ width: '100%', padding: UI.spacing.md, border: `1px solid ${UI.colors.border}`, borderRadius: UI.radius.md, fontSize: 13 }} />
                    </div>
                    <div style={{ marginBottom: UI.spacing.lg }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Description</label>
                      <textarea value={formData.description || ''} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={3} style={{ width: '100%', padding: UI.spacing.md, border: `1px solid ${UI.colors.border}`, borderRadius: UI.radius.md, fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }} />
                    </div>
                    <div style={{ marginBottom: UI.spacing.lg }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Status</label>
                      <div style={{ display: 'flex', gap: UI.spacing.md }}>
                        <button onClick={() => setFormData({ ...formData, status: 'draft' })} style={{ padding: UI.spacing.md, flex: 1, background: formData.status === 'draft' ? UI.colors.primary : '#FFFFFF', border: `1px solid ${UI.colors.border}`, borderRadius: UI.radius.md, fontSize: 13, fontWeight: 600, color: formData.status === 'draft' ? '#FFFFFF' : UI.colors.secondary, cursor: 'pointer' }}>Draft</button>
                        <button onClick={() => setFormData({ ...formData, status: 'published' })} style={{ padding: UI.spacing.md, flex: 1, background: formData.status === 'published' ? UI.colors.primary : '#FFFFFF', border: `1px solid ${UI.colors.border}`, borderRadius: UI.radius.md, fontSize: 13, fontWeight: 600, color: formData.status === 'published' ? '#FFFFFF' : UI.colors.secondary, cursor: 'pointer' }}>Published</button>
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: UI.spacing.lg }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: UI.spacing.md }}>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>Sections</div>
                        <select onChange={e => handleAddSection(e.target.value)} style={{ padding: UI.spacing.sm, border: `1px solid ${UI.colors.border}`, borderRadius: UI.radius.md, fontSize: 12 }}>
                          <option value="">+ Add Section</option>
                          {sectionTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                        </select>
                      </div>
                      {sections.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: UI.spacing.xl, color: UI.colors.muted, background: UI.colors.bg, borderRadius: UI.radius.md }}>
                          No sections yet. Click "Add Section" to add content.
                        </div>
                      ) : (
                        sections.map((section, idx) => (
                          <div key={section.id} style={{ background: UI.colors.bg, borderRadius: UI.radius.md, padding: UI.spacing.md, marginBottom: UI.spacing.sm }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: UI.spacing.sm }}>
                              <div style={{ fontWeight: 600 }}>{sectionTypes.find(t => t.value === section.type)?.label || section.type}</div>
                              <div style={{ display: 'flex', gap: UI.spacing.xs }}>
                                <button onClick={() => handleMoveSection(section.id, 'up')} disabled={idx === 0} style={{ width: 28, height: 28, borderRadius: UI.radius.sm, border: `1px solid ${UI.colors.border}`, background: '#FFFFFF', cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.5 : 1 }}>↑</button>
                                <button onClick={() => handleMoveSection(section.id, 'down')} disabled={idx === sections.length - 1} style={{ width: 28, height: 28, borderRadius: UI.radius.sm, border: `1px solid ${UI.colors.border}`, background: '#FFFFFF', cursor: idx === sections.length - 1 ? 'not-allowed' : 'pointer', opacity: idx === sections.length - 1 ? 0.5 : 1 }}>↓</button>
                                <button onClick={() => setEditingSection(section)} style={{ width: 28, height: 28, borderRadius: UI.radius.sm, border: `1px solid ${UI.colors.border}`, background: '#FFFFFF', cursor: 'pointer' }}>✏️</button>
                                <button onClick={() => handleDeleteSection(section.id)} style={{ width: 28, height: 28, borderRadius: UI.radius.sm, border: `1px solid ${UI.colors.border}`, background: '#FFFFFF', cursor: 'pointer' }}>🗑️</button>
                              </div>
                            </div>
                            <div style={{ fontSize: 12, color: UI.colors.muted }}>Position: {section.position}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: UI.spacing.lg }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Meta Title ({formData.meta_title?.length || 0}/60)</label>
                      <input type="text" value={formData.meta_title || ''} onChange={e => setFormData({ ...formData, meta_title: e.target.value })} style={{ width: '100%', padding: UI.spacing.md, border: `1px solid ${(formData.meta_title?.length || 0) > 60 ? UI.colors.danger : UI.colors.border}`, borderRadius: UI.radius.md, fontSize: 13 }} />
                    </div>
                    <div style={{ marginBottom: UI.spacing.lg }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Meta Description ({formData.meta_description?.length || 0}/160)</label>
                      <textarea value={formData.meta_description || ''} onChange={e => setFormData({ ...formData, meta_description: e.target.value })} rows={3} style={{ width: '100%', padding: UI.spacing.md, border: `1px solid ${(formData.meta_description?.length || 0) > 160 ? UI.colors.danger : UI.colors.border}`, borderRadius: UI.radius.md, fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }} />
                    </div>
                    <div style={{ marginBottom: UI.spacing.lg }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Schema Type</label>
                      <select value={formData.schema_type} onChange={e => setFormData({ ...formData, schema_type: e.target.value })} style={{ width: '100%', padding: UI.spacing.md, border: `1px solid ${UI.colors.border}`, borderRadius: UI.radius.md, fontSize: 13 }}>
                        <option value="WebPage">WebPage</option>
                        <option value="FAQPage">FAQPage</option>
                        <option value="LocalBusiness">LocalBusiness</option>
                        <option value="Service">Service</option>
                      </select>
                    </div>
                    <div style={{ marginBottom: UI.spacing.lg }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Category (for LocalBusiness)</label>
                      <input type="text" value={formData.category || ''} onChange={e => setFormData({ ...formData, category: e.target.value })} style={{ width: '100%', padding: UI.spacing.md, border: `1px solid ${UI.colors.border}`, borderRadius: UI.radius.md, fontSize: 13 }} />
                    </div>
                    <div style={{ marginBottom: UI.spacing.lg }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.sm }}>Location (for LocalBusiness)</label>
                      <input type="text" value={formData.location || ''} onChange={e => setFormData({ ...formData, location: e.target.value })} style={{ width: '100%', padding: UI.spacing.md, border: `1px solid ${UI.colors.border}`, borderRadius: UI.radius.md, fontSize: 13 }} />
                    </div>
                    
                    <div style={{ background: UI.colors.bg, borderRadius: UI.radius.md, padding: UI.spacing.lg, marginTop: UI.spacing.lg }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: UI.spacing.md }}>Search Preview</div>
                      <div style={{ fontFamily: 'Arial, sans-serif' }}>
                        <div style={{ fontSize: 18, color: '#1A0DAB', marginBottom: UI.spacing.xs }}>{formData.meta_title || formData.title}</div>
                        <div style={{ fontSize: 14, color: '#006621', marginBottom: UI.spacing.xs }}>{window.location.origin}/{formData.slug}</div>
                        <div style={{ fontSize: 13, color: '#545454' }}>{formData.meta_description || 'No description provided'}</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* New Page Modal */}
      <NewPageModal
        isOpen={newPageModalOpen}
        onClose={() => setNewPageModalOpen(false)}
        onSave={handleCreatePage}
      />

      {/* Section Editor Drawer */}
      {editingSection && (
        <SectionEditor
          section={editingSection}
          onSave={handleUpdateSection}
          onClose={() => setEditingSection(null)}
        />
      )}
      
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: toast.type === 'error' ? UI.colors.danger : UI.colors.success, color: '#FFFFFF', padding: `${UI.spacing.md}px ${UI.spacing.lg}px`, borderRadius: UI.radius.md, fontSize: 13, fontWeight: 600, zIndex: 999, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}